/* Copyright (c) 2021-2022 VMware, Inc. All rights reserved. VMware Confidential */

import { Observable } from "rxjs";
import { Injectable } from "@angular/core";
import { HttpWrapperService, RbacService } from "@app/services";
import { UiPrivilegeEnum } from "./roles.model";

type HttpFunc<T> = ((string, boolean?) => Observable<T>) | ((string, any?, boolean?) => Observable<T>);
type HttpFuncResult<T> = (...args) => Observable<T>;


// @formatter:off
@Injectable({
    providedIn: "root"
})
// @formatter:on
export class RbacHttpService {
    constructor(private http: HttpWrapperService,
                private rbacService: RbacService) {
    }

    /**
     * Return a function that conditionally checks that the user has the exact required privilege before executing
     * an HTTP GET request.
     *
     * @param privilege: UiPrivilegeEnum
     * @param emptyResult: any
     */
    public get<T>(privilege: UiPrivilegeEnum, emptyResult: any): HttpFuncResult<T> {
        const canRequest: boolean = (!privilege || this.rbacService.hasPrivilege(privilege));
        return this.httpWrapper<T>(this.http.get, canRequest, emptyResult);
    }

    /**
     * Return a function that conditionally checks that the user has the exact required privilege before executing
     * an HTTP POST request.
     *
     * @param privilege: UiPrivilegeEnum
     * @param emptyResult: any
     */
    public post<T>(privilege: UiPrivilegeEnum, emptyResult: any): HttpFuncResult<T> {
        const canRequest: boolean = (!privilege || this.rbacService.hasPrivilege(privilege));
        return this.httpWrapper<T>(this.http.post, canRequest, emptyResult);
    }

    /**
     * Return a function that conditionally checks that the user has the exact required privilege before executing
     * an HTTP PATCH request.
     *
     * @param privilege: UiPrivilegeEnum
     * @param emptyResult: any
     */
    public patch<T>(privilege: UiPrivilegeEnum, emptyResult: any): HttpFuncResult<T> {
        const canRequest: boolean = (!privilege || this.rbacService.hasPrivilege(privilege));
        return this.httpWrapper<T>(this.http.patch, canRequest, emptyResult);
    }

    /**
     * Return a function that conditionally checks that the user has the exact required privilege before executing
     * an HTTP DELETE request.
     *
     * @param privilege: UiPrivilegeEnum
     * @param emptyResult: any
     */
    public delete<T>(privilege: UiPrivilegeEnum, emptyResult: any): HttpFuncResult<T> {
        const canRequest: boolean = (!privilege || this.rbacService.hasPrivilege(privilege));
        return this.httpWrapper<T>(this.http.delete, canRequest, emptyResult);
    }

    /**
     * Return a wrapper function for any HttpClient function (get, post, delete, etc.). If canRequest is false, the
     * function will return an Observable with an empty result.
     *
     * @param httpFunc: Function - HttpClient function
     * @param canRequest: boolean
     * @param emptyResult: any
     * @private
     */
    private httpWrapper<T>(httpFunc: HttpFunc<T>, canRequest: boolean, emptyResult: any): HttpFuncResult<T> {
        const httpContext: HttpWrapperService = this.http;
        return function(...args): Observable<T> {
            if (!canRequest) {
                const emptyObservable: Observable<T> = new Observable<any>(subscriber => {
                    subscriber.next(emptyResult);
                });
                return emptyObservable;
            }
            return httpFunc.apply(httpContext, args);
        };
    }
}
